/**
 * Utilitário para aplicar cores do tema escuro em HTML com estilos inline.
 * Usado por HtmlEditorContent (exibição) e SummernoteEditor (edição).
 *
 * Centraliza o de-para de cores (background-color e color) para exibição
 * correta quando o tema escuro está ativo.
 */

import { summernotePaletteContrastDark } from '../assets/plugins/summernote/palette-contrast-dark';

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

/**
 * Retorna cópia do objeto com todas as chaves em maiúsculas (lookup case-insensitive).
 * @param {Record<string, string>} obj - Mapa cor original → cor para dark
 * @returns {Record<string, string>}
 */
function mapKeysToUpperCase(obj) {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k.toUpperCase(), v])
  );
}

/** Cores default para tema dark (base + paleta Summernote). */
const DARK_THEME_DEFAULT_MAP = {
  '#2D3B45': '#E3E3E3',
  '#00FFFF': '#00FFFF',
  '#F1C40F': '#F1C40F',
  '#FBEEB8': '#FBEEB8',
  '#FFE79C': '#FFE79C',
  '#FFFF00': '#FFFF00',
  '#FFFFFF': '#FFFFFF',
  '#23527C': '#427CAF',
  '#434343': '#878787',
  '#9CA3AF': '#9CA3AF',
  '#1155CC': '#1155CC',
};

const DARK_THEME_BACKGROUND_MAP = mapKeysToUpperCase({
  ...DARK_THEME_DEFAULT_MAP,
  ...summernotePaletteContrastDark,
});

const DARK_THEME_COLOR_MAP = mapKeysToUpperCase({
  ...DARK_THEME_DEFAULT_MAP,
  ...summernotePaletteContrastDark,
});

/** Em development, loga cores sem de-para no console. */
const DEBUG_UNMAPPED_COLORS = process.env.NODE_ENV === 'development';

/**
 * Converte r, g, b (0–255) para string hex #rrggbb.
 * @param {number} r - Vermelho 0–255
 * @param {number} g - Verde 0–255
 * @param {number} b - Azul 0–255
 * @returns {string} Cor em hex, ex: #2d3b45
 */
function rgbToHex(r, g, b) {
  const clamp = (n) => Math.max(0, Math.min(255, parseInt(n, 10)));
  const toHex = (n) => ('0' + clamp(n).toString(16)).slice(-2);
  return '#' + toHex(r) + toHex(g) + toHex(b);
}

/**
 * Normaliza cor (hex ou rgb) para hex em MAIÚSCULAS para lookup.
 * @param {string} value - "#rrggbb" ou "rgb(r,g,b)"
 * @returns {string|null} "#RRGGBB" ou null se inválido
 */
function normalizeColorToHex(value) {
  if (!value) return null;
  const trimmed = value.trim();
  const hexMatch = trimmed.match(/^#([\da-fA-F]{6})$/);
  if (hexMatch) return ('#' + hexMatch[1]).toUpperCase();
  const rgbMatch = trimmed.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/);
  if (rgbMatch) return rgbToHex(rgbMatch[1], rgbMatch[2], rgbMatch[3]).toUpperCase();
  return null;
}

// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------

/**
 * Aplica o de-para de cores (background-color e color) no HTML para tema escuro.
 * @param {string} html - Conteúdo HTML com estilos inline
 * @returns {string} HTML com cores substituídas
 */
export function applyDarkThemeColors(html) {
  if (!html || typeof html !== 'string') return html;

  return html.replace(/\bstyle\s*=\s*["']([^"']*)["']/gi, (match, styleContent) => {
    let newStyle = styleContent;

    newStyle = newStyle.replace(
      /(background-color\s*:\s*)(#[\da-fA-F]{6}|rgb\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\))(?=[\s;]|$)/gi,
      (propMatch, prefix, value) => {
        const hexKey = normalizeColorToHex(value);
        if (!hexKey) return propMatch;
        const toHex = DARK_THEME_BACKGROUND_MAP[hexKey];
        return toHex ? `${prefix}${toHex}` : propMatch;
      }
    );
    newStyle = newStyle.replace(
      /(color\s*:\s*)(#[\da-fA-F]{6}|rgb\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\))(?=[\s;]|$)/gi,
      (propMatch, prefix, value) => {
        const hexKey = normalizeColorToHex(value);
        if (!hexKey) return propMatch;
        const toHex = DARK_THEME_COLOR_MAP[hexKey];
        return toHex ? `${prefix}${toHex}` : propMatch;
      }
    );

    return `style="${newStyle}"`;
  });
}

/**
 * Em development, lista em console.warn as cores (background-color e color) sem de-para.
 * @param {string} html - Conteúdo HTML com estilos inline
 * @param {string} [logPrefix='[htmlDarkThemeColors]'] - Prefixo das mensagens de log
 */
export function logUnmappedColors(html, logPrefix = '[htmlDarkThemeColors]') {
  if (!DEBUG_UNMAPPED_COLORS || !html || typeof html !== 'string') return;

  const unmappedBg = new Set();
  const unmappedColor = new Set();

  html.replace(/\bstyle\s*=\s*["']([^"']*)["']/gi, (match, styleContent) => {
    styleContent.replace(
      /(background-color\s*:\s*)(#[\da-fA-F]{6}|rgb\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\))(?=[\s;]|$)/gi,
      (propMatch, _prefix, value) => {
        const hexKey = normalizeColorToHex(value);
        if (hexKey && !(hexKey in DARK_THEME_BACKGROUND_MAP)) unmappedBg.add(hexKey);
        return propMatch;
      }
    );
    styleContent.replace(
      /(color\s*:\s*)(#[\da-fA-F]{6}|rgb\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\))(?=[\s;]|$)/gi,
      (propMatch, _prefix, value) => {
        const hexKey = normalizeColorToHex(value);
        if (hexKey && !(hexKey in DARK_THEME_COLOR_MAP)) unmappedColor.add(hexKey);
        return propMatch;
      }
    );
    return match;
  });

  unmappedBg.forEach((hex) => {
    console.warn(`${logPrefix} Cor sem de-para (background-color):`, hex);
  });
  unmappedColor.forEach((hex) => {
    console.warn(`${logPrefix} Cor sem de-para (color):`, hex);
  });
}
